'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { ArrowLeft, Eye, EyeOff, RefreshCw, Sparkles } from 'lucide-react'
import Image from 'next/image'
import Api from '@/api/h5Api'
import { useAuth } from '@/lib/auth-context'

interface ApiResponse<T = any> {
  code: number
  message?: string
  data: T
}

export default function LoginPage() {
  const router = useRouter()
  const { setAuthUser } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [captchaId, setCaptchaId] = useState('')
  const [captcha, setCaptcha] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [debugCount, setDebugCount] = useState(0)
  const [isDebugMode, setIsDebugMode] = useState(false)
  const [debugPassword, setDebugPassword] = useState('')

  // 获取验证码
  const getCaptcha = async () => {
    try {
      const res = (await Api.userCaptcha({})) as unknown as ApiResponse
      if (res.code === 200) {
        setCaptcha(res.data.captcha)
        setCaptchaId(res.data.captchaId)
      }
    } catch (error) {
      console.error('获取验证码失败:', error)
    }
  }

  // 处理登录
  const handleLogin = async () => {
    if (username.trim() === '') {
      alert('用户名不能为空')
      return
    }
    if (password.trim() === '') {
      alert('密码不能为空')
      return
    }
    if (code.trim() === '') {
      alert('验证码不能为空')
      return
    }

    setIsLoading(true)
    try {
      const res = (await Api.userAiLogin(
        {
          username,
          password,
          code,
          captchaId,
          isDebuger: isDebugMode,
          debugerPassword: debugPassword,
        },
        true
      )) as unknown as ApiResponse

      if (res.code === 200) {
        // 存储用户信息到 localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem('username', res.data.username)
          localStorage.setItem('nickname', res.data.nickname)
          localStorage.setItem('token', res.data.token)
          localStorage.setItem('talkId', res.data.talkId)
          localStorage.setItem('uid', res.data.uid)
          localStorage.removeItem('isGuest')
        }
        // 同步 auth context，让 Header 立即更新
        setAuthUser({ id: res.data.uid, username: res.data.nickname || res.data.username, email: res.data.username })
        // 跳转到首页
        router.push('/light')
      }
    } catch (error) {
      console.error('登录失败:', error)
      getCaptcha() // 重新获取验证码
    } finally {
      setIsLoading(false)
    }
  }

  // 游客登录
  const handleGuestLogin = async () => {
    setIsLoading(true)
    try {
      const guestRes = (await Api.userAiGuestAdd({}, false)) as unknown as ApiResponse
      if (guestRes.code === 200) {
        const { username, password } = guestRes.data
        const loginRes = (await Api.userAiLogin(
          {
            username,
            password,
            isGuest: true,
            isDebuger: isDebugMode,
            debugerPassword: debugPassword,
          },
          false
        )) as unknown as ApiResponse

        if (loginRes.code === 200) {
          if (typeof window !== 'undefined') {
            localStorage.setItem('username', loginRes.data.username)
            localStorage.setItem('nickname', loginRes.data.nickname)
            localStorage.setItem('token', loginRes.data.token)
            localStorage.setItem('talkId', loginRes.data.talkId)
            localStorage.setItem('uid', loginRes.data.uid)
            localStorage.setItem('isGuest', '1')
          }
          // 同步 auth context，让 Header 立即更新
          setAuthUser({ id: loginRes.data.uid, username: loginRes.data.nickname || loginRes.data.username, email: loginRes.data.username })
          router.push('/light')
        }
      }
    } catch (error) {
      console.error('游客登录失败:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // 处理调试模式
  const handleDebugClick = () => {
    setDebugCount((prev) => prev + 1)
    if (debugCount >= 5) {
      setIsDebugMode(true)
      if (typeof window !== 'undefined') {
        localStorage.setItem('isDebuger', '1')
      }
    }
  }

  // Enter 键登录
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin()
    }
  }

  useEffect(() => {
    getCaptcha()
    // 检查是否已经是调试模式
    if (typeof window !== 'undefined') {
      const savedDebugMode = localStorage.getItem('isDebuger')
      if (savedDebugMode === '1') {
        setIsDebugMode(true)
      }
    }
  }, [])

  return (
    <main className="min-h-screen bg-linear-to-br from-primary/5 via-background to-secondary/5 relative overflow-hidden">
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse-slow" />
        <div
          className="absolute bottom-20 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl animate-pulse-slow"
          style={{ animationDelay: '2s' }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent/5 rounded-full blur-3xl animate-pulse-slow"
          style={{ animationDelay: '4s' }}
        />
      </div>

      {/* 内容区域 */}
      <div className="relative flex flex-col items-center justify-center min-h-screen px-4 py-12">
        {/* 返回按钮 */}
        <button
          onClick={() => router.push('/light')}
          className="group absolute top-6 left-6 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-card/60 backdrop-blur-xl border border-border/50 hover:border-primary/50 shadow-sm hover:shadow-md transition-all duration-300"
        >
          <ArrowLeft className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors duration-300 group-hover:-translate-x-0.5" />
          <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors duration-300">
            返回首页
          </span>
        </button>

        {/* 登录卡片 */}
        <div className="w-full max-w-md bg-card/90 backdrop-blur-2xl rounded-3xl border-2 border-primary/20 shadow-2xl shadow-primary/10 p-8 animate-fade-in-up">
          {/* Logo 和标题 */}
          <div className="text-center mb-8">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-linear-to-br from-primary to-secondary shadow-lg mb-4 cursor-pointer"
              onClick={handleDebugClick}
            >
              <Sparkles className="w-8 h-8 text-primary-foreground animate-pulse" />
            </div>
            <h1 className="text-2xl font-bold bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
              AI 智能学习平台
            </h1>
            <p className="text-sm text-muted-foreground">欢迎登录</p>
          </div>

          {/* 表单 */}
          <div className="space-y-4">
            {/* 用户名 */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">邮箱/账号</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="请输入邮箱或账号"
                className="w-full px-4 py-3 rounded-xl bg-background/50 border-2 border-border focus:border-primary focus:outline-none transition-all duration-300 text-foreground placeholder:text-muted-foreground"
                autoFocus
              />
            </div>

            {/* 密码 */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">密码</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="请输入密码"
                  className="w-full px-4 py-3 rounded-xl bg-background/50 border-2 border-border focus:border-primary focus:outline-none transition-all duration-300 text-foreground placeholder:text-muted-foreground pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* 验证码 */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">验证码</label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="请输入验证码"
                  maxLength={6}
                  className="flex-1 px-4 py-3 rounded-xl bg-background/50 border-2 border-border focus:border-primary focus:outline-none transition-all duration-300 text-foreground placeholder:text-muted-foreground"
                />
                <button
                  onClick={getCaptcha}
                  className="flex-shrink-0 w-32 h-12 rounded-xl bg-background/80 border-2 border-border hover:border-primary transition-all duration-300 overflow-hidden flex items-center justify-center group"
                  dangerouslySetInnerHTML={{ __html: captcha }}
                />
              </div>
            </div>

            {/* 调试模式 */}
            {isDebugMode && (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">调试密码</label>
                  <input
                    type="password"
                    value={debugPassword}
                    onChange={(e) => setDebugPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="请输入调试密码"
                    className="w-full px-4 py-3 rounded-xl bg-background/50 border-2 border-border focus:border-primary focus:outline-none transition-all duration-300 text-foreground placeholder:text-muted-foreground"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setIsDebugMode(false)
                      setDebugCount(0)
                      if (typeof window !== 'undefined') {
                        localStorage.setItem('isDebuger', '2')
                      }
                    }}
                    className="px-4 py-2 rounded-lg bg-muted/50 hover:bg-muted text-sm text-muted-foreground hover:text-foreground transition-all duration-300"
                  >
                    关闭调试模式
                  </button>
                  <span className="text-xs text-muted-foreground">调试模式已开启</span>
                </div>
              </>
            )}

            {/* 登录按钮 */}
            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full group relative overflow-hidden rounded-xl bg-linear-to-r from-primary to-secondary p-0.5 hover:shadow-xl hover:shadow-primary/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none hover:scale-[1.02]"
            >
              <div className="relative bg-card/90 backdrop-blur-sm rounded-xl px-6 py-3 group-hover:bg-transparent transition-all duration-300">
                <span className="flex items-center justify-center gap-2 text-foreground group-hover:text-primary-foreground transition-colors duration-300 font-semibold">
                  {isLoading ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      登录中...
                    </>
                  ) : (
                    '登录'
                  )}
                </span>
              </div>
            </button>

            {/* 底部链接 */}
            <div className="flex flex-wrap items-center justify-center gap-4 pt-4 border-t border-border/50">
              <button
                onClick={() => router.push('/light/register')}
                className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
              >
                注册账号
              </button>
              <span className="text-muted-foreground">•</span>
              <button
                onClick={handleGuestLogin}
                disabled={isLoading}
                className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300 disabled:opacity-50"
              >
                游客登录
              </button>
              <span className="text-muted-foreground">•</span>
              <button
                onClick={() => router.push('/light/forgot-password')}
                className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
              >
                忘记密码
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}
